import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiCreditCard } from 'react-icons/fi';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import {
  Input,
  Textarea,
  Select,
  NumberInput,
} from '../../components/ui/Input';
import { listInvoices, getInvoice } from '../../api/invoices';
import { recordPayment, updatePayment } from '../../api/payments';
import { PaymentMethod, enumOptions } from '../../enums';
import { extractErrorMessage } from '../../api/client';
import { formatCurrency } from '../../utils/currency';

const METHOD_OPTIONS = enumOptions(PaymentMethod);

function nowLocalIsoMinute() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function remainingOf(inv) {
  if (!inv) return null;
  const total = Number(inv.totalAmount ?? inv.amount ?? 0);
  const paid = Number(inv.paidAmount ?? 0);
  return Math.max(total - paid, 0);
}

export default function RecordPaymentForm({
  open,
  onClose,
  defaultInvoiceId = null,
  editPayment = null,
}) {
  const qc = useQueryClient();
  const [invoiceId, setInvoiceId] = useState(defaultInvoiceId || '');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.BANK_TRANSFER);
  const [referenceNo, setReferenceNo] = useState('');
  const [notes, setNotes] = useState('');
  const [paidAt, setPaidAt] = useState(nowLocalIsoMinute());
  const [errors, setErrors] = useState({});

  // Reset state when the modal is opened (so re-opens start clean).
  useEffect(() => {
    if (!open) return;
    if (editPayment) {
      setInvoiceId(editPayment.invoiceId || defaultInvoiceId || '');
      setAmount(editPayment.amount || '');
      setPaymentMethod(editPayment.paymentMethod || editPayment.paymentMode || PaymentMethod.BANK_TRANSFER);
      setReferenceNo(editPayment.referenceNo || '');
      setNotes(editPayment.notes || '');
      setPaidAt(editPayment.paidAt ? new Date(editPayment.paidAt).toISOString().slice(0, 16) : nowLocalIsoMinute());
    } else {
      setInvoiceId(defaultInvoiceId || '');
      setAmount('');
      setPaymentMethod(PaymentMethod.BANK_TRANSFER);
      setReferenceNo('');
      setNotes('');
      setPaidAt(nowLocalIsoMinute());
    }
    setErrors({});
  }, [open, defaultInvoiceId, editPayment]);

  const invoicesQuery = useQuery({
    queryKey: ['invoices'],
    queryFn: () => listInvoices(),
    enabled: open && !defaultInvoiceId,
    staleTime: 30_000,
  });

  // Pull a single invoice when we have an invoiceId so we can show its remaining
  // balance inside the modal (handy for both pre-set + picker flows).
  const invoiceQuery = useQuery({
    queryKey: ['invoices', Number(invoiceId)],
    queryFn: () => getInvoice(invoiceId),
    enabled: open && !!invoiceId,
    staleTime: 10_000,
  });

  const selectedInvoice = invoiceQuery.data;
  const remaining = remainingOf(selectedInvoice);

  const invoiceOptions = useMemo(() => {
    const list = invoicesQuery.data || [];
    return list.map((inv) => {
      const code = inv.invoiceNumber || `INV-${inv.id}`;
      const rem = remainingOf(inv);
      return {
        value: inv.id,
        label: `${code} · Cust ${inv.customerId ?? '—'} · Balance ${formatCurrency(rem ?? 0)}`,
      };
    });
  }, [invoicesQuery.data]);

  const mutation = useMutation({
    mutationFn: (payload) => editPayment ? updatePayment(editPayment.id, payload) : recordPayment(payload),
    onSuccess: () => {
      toast.success(editPayment ? 'Payment updated' : 'Payment recorded');
      const idNum = Number(invoiceId);
      qc.invalidateQueries({ queryKey: ['payments', idNum] });
      qc.invalidateQueries({ queryKey: ['invoices', idNum] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['revenue'] });
      onClose?.();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const validate = () => {
    const e = {};
    if (!invoiceId) e.invoiceId = 'Select an invoice';
    const amt = Number(amount);
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      e.amount = 'Amount must be greater than zero';
    } else if (remaining != null && !editPayment && amt > remaining + 0.0001) {
      e.amount = `Amount exceeds remaining balance (${formatCurrency(remaining)})`;
    }
    if (!paymentMethod) e.paymentMethod = 'Select a payment method';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev?.preventDefault?.();
    if (!validate()) return;
    const payload = {
      invoiceId: Number(invoiceId),
      amount: Number(amount),
      paymentMethod,
      referenceNo: referenceNo.trim() || undefined,
      notes: notes.trim() || undefined,
      paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
    };
    mutation.mutate(payload);
  };

  const submitting = mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={editPayment ? "Edit Payment" : "Record Payment"}
      description={editPayment ? "Update the payment details." : "Log a payment received against an invoice."}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={FiCreditCard}
            onClick={handleSubmit}
            loading={submitting}
          >
            {editPayment ? "Save Changes" : "Record Payment"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Invoice"
          required
          error={errors.invoiceId}
          className="sm:col-span-2"
        >
          {defaultInvoiceId ? (
            <Input
              value={
                selectedInvoice
                  ? `${selectedInvoice.invoiceNumber || `INV-${selectedInvoice.id}`} · Cust ${selectedInvoice.customerId ?? '—'}`
                  : `INV-${defaultInvoiceId}`
              }
              disabled
            />
          ) : (
            <Select
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              error={errors.invoiceId}
              disabled={invoicesQuery.isLoading}
            >
              <option value="">
                {invoicesQuery.isLoading ? 'Loading invoices…' : 'Select an invoice'}
              </option>
              {invoiceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        {selectedInvoice && (
          <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-ink-muted">
            <div className="flex items-center justify-between gap-3">
              <span>
                Total: <span className="font-medium text-ink">{formatCurrency(selectedInvoice.totalAmount ?? selectedInvoice.amount ?? 0)}</span>
              </span>
              <span>
                Paid: <span className="font-medium text-ink">{formatCurrency(selectedInvoice.paidAmount ?? 0)}</span>
              </span>
              <span>
                Remaining: <span className="font-semibold text-primary">{formatCurrency(remaining ?? 0)}</span>
              </span>
            </div>
          </div>
        )}

        <FormField label="Amount" required error={errors.amount}>
          <NumberInput
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
            step="0.01"
            placeholder="0.00"
            error={errors.amount}
          />
        </FormField>

        <FormField label="Payment Method" required error={errors.paymentMethod}>
          <Select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            error={errors.paymentMethod}
          >
            {METHOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Reference No" hint="Transaction / cheque / UPI ref">
          <Input
            value={referenceNo}
            onChange={(e) => setReferenceNo(e.target.value)}
            placeholder="e.g. TXN12345"
          />
        </FormField>

        <FormField label="Paid At">
          <Input
            type="datetime-local"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </FormField>

        <FormField label="Notes" className="sm:col-span-2">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Optional notes about this payment"
          />
        </FormField>
      </form>
    </Modal>
  );
}
